import { useRecordActivity } from '@/api/callcenter/assignments/recordActivity';
import { QueueItem } from '@/api/callcenter/dashboard';
import { toast } from '@/components/ui/elements/use-toast';
import {
  BrightPatternApi,
  BrightPatternStatus,
  getBrightPatternStatus,
  loadBrightPatternSdk,
} from '@/features/callcenter/lib/brightpattern';
import { useEffect, useRef, useState } from 'react';

// How long a click may go unanswered by the SDK's _O_CALL_DIALING broadcast
// before we tell the agent the dial silently died. The failure modes here
// (extension missing or site-restricted, Agent Desktop not open in this
// browser, partitioned storage) all produce NO error anywhere -- this
// watchdog is the only feedback the agent gets.
export const DIAL_FEEDBACK_TIMEOUT_MS = 3000;

/**
 * Owns the BrightPattern Agent Desktop SDK lifecycle for click-to-call: kicks
 * off the loader once on mount (skipped entirely when unconfigured, so an
 * unconfigured deployment never touches the loader at all), tracks its
 * readiness, and exposes a single `dial` entry point that sequences
 * selectService -> dialNumber -> a 'dialing' activity ping. The frontend
 * never reports call outcomes itself -- those flow back through backend
 * webhooks once BrightPattern completes the call.
 */
export const useBrightPatternDial = () => {
  const [status, setStatus] = useState<BrightPatternStatus>(() => getBrightPatternStatus());
  const apiRef = useRef<BrightPatternApi | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { mutate: recordActivity } = useRecordActivity();

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  useEffect(() => {
    if (getBrightPatternStatus() === 'unconfigured') {
      return;
    }

    let cancelled = false;

    loadBrightPatternSdk()
      .then((api) => {
        if (cancelled) return;
        apiRef.current = api;
        // Any genuine dial start (including ones initiated inside Agent
        // Desktop itself) proves the bridge is alive -- stand down the
        // pending watchdog.
        api.addOutboundCallDialingHandler(() => clearWatchdog());
        setStatus('ready');
      })
      .catch(() => {
        // Expected for an unconfigured tenant (BrightPatternUnconfiguredError)
        // and for a genuine script/init failure alike -- either way there is
        // nothing more to do here than reflect it in status. Never rethrown,
        // so this can't surface as an unhandled rejection or crash React.
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
      clearWatchdog();
    };
  }, []);

  const dial = (item: QueueItem) => {
    if (status !== 'ready') return;
    // A phone system can't dial a number it doesn't have -- guards past
    // this point narrow phone_e164 to a plain string for the api call below.
    // QueueCard and AssignmentActivityTracker already hide the Call button
    // entirely for this case (same treatment as no dial payload at all), so
    // this is defense in depth for any future caller that reaches dial()
    // without going through one of those buttons.
    if (!item.dial?.phone_e164) return;
    if (!item.assignment_id) return;

    const api = apiRef.current;
    if (!api) return;

    try {
      api.selectService(item.dial.bp_service_name);
      api.dialNumber(item.dial.phone_e164);
    } catch {
      // A throwing bpspat call (e.g. the agent was logged out mid-session)
      // must not crash the dashboard -- fall back to the disabled/tooltip
      // state instead.
      setStatus('error');
      return;
    }

    // Fire-and-forget: postActivity already opts out of the global error
    // toast (skipErrorToast) and useRecordActivity adds no invalidation or
    // toast of its own, so a failed 'dialing' ping -- non-actionable by
    // design -- needs no onError/onSuccess handling here.
    recordActivity({ assignmentId: item.assignment_id, type: 'dialing' });

    // The SDK's dial commands report nothing back; only the
    // _O_CALL_DIALING broadcast (cleared above) proves the call started.
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = null;
      toast({
        title: "Call didn't start",
        description:
          'BrightPattern Agent Desktop did not respond. Check that it is open ' +
          'in this browser and its Chrome extension is allowed on all sites — ' +
          'or dial manually.',
      });
    }, DIAL_FEEDBACK_TIMEOUT_MS);
  };

  return { status, dial };
};
